import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { actors } from '@/mocks/actors';
import { films } from '@/mocks/films';

export default function ActorPage() {
  const { id } = useParams();
  const actor = useMemo(() => actors.find((a) => a.id === id), [id]);

  useEffect(() => {
    document.title = actor
      ? `${actor.name} 演员资料 - 姐姐的朋友影视内容站`
      : '演员资料不存在 - 姐姐的朋友影视内容站';
  }, [actor]);

  const works = useMemo(() => {
    if (!actor) return [];
    return films
      .filter((f) => actor.filmIds.includes(f.id))
      .map((f) => {
        const member = f.cast.find((c) => c.actorId === actor.id);
        return { film: f, role: member ? member.role : '参演' };
      });
  }, [actor]);

  if (!actor) {
    return (
      <div className="pt-16 md:pt-[72px]">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="flex items-center justify-center w-16 h-16 rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-user-unfollow-line text-3xl" />
          </span>
          <h1 className="font-heading font-bold text-xl text-foreground-900 mb-2">暂无演员资料</h1>
          <p className="text-sm text-foreground-500 mb-6">该演员的资料可能尚未收录</p>
          <Link
            to="/"
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-[72px]">
      <div className="bg-primary-950">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '演员资料' }, { label: actor.name }]} />
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="flex flex-col md:flex-row gap-8 md:gap-12">
          <div className="w-44 md:w-64 shrink-0 mx-auto md:mx-0">
            <div className="w-full aspect-[3/4] rounded-lg overflow-hidden border border-background-200 bg-background-200">
              <img
                src={actor.photo}
                alt={`演员 ${actor.name}`}
                title={`${actor.name} 演员资料`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-950 mb-4">
              {actor.name}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-700">
                {actor.profession}
              </span>
              <span className="px-2.5 py-1 rounded-md text-xs bg-secondary-100 text-secondary-900">
                {actor.region}
              </span>
              <span className="px-2.5 py-1 rounded-md text-xs bg-background-200 text-foreground-700">
                {actor.birthYear} 年出生
              </span>
            </div>

            <h2 className="font-heading font-semibold text-base text-foreground-950 mb-3">职业简介</h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose mb-6 max-w-3xl">{actor.bio}</p>

            <div className="flex flex-wrap gap-6">
              <div>
                <p className="font-heading font-black text-2xl text-accent-600">{works.length}</p>
                <p className="text-xs text-foreground-500 mt-1">收录作品</p>
              </div>
              <div>
                <p className="font-heading font-black text-2xl text-accent-600">
                  {actor.birthYear ? `${new Date().getFullYear() - actor.birthYear}` : '—'}
                </p>
                <p className="text-xs text-foreground-500 mt-1">年龄（约）</p>
              </div>
            </div>
          </div>
        </div>

        <section className="mt-12">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            代表作
          </h2>
          {works.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              {works.map(({ film, role }) => (
                <Link
                  key={film.id}
                  to={`/detail/${film.id}`}
                  className="group flex gap-4 p-4 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="w-20 h-28 md:w-24 md:h-32 shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img
                      src={film.cover}
                      alt={`${film.title} 海报`}
                      title={`${film.title} 剧情介绍`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-col justify-center gap-2 min-w-0">
                    <h3 className="font-heading font-semibold text-base text-foreground-950 group-hover:text-primary-700 transition-colors line-clamp-1">
                      {film.title}
                    </h3>
                    <p className="text-sm text-accent-600">{role}</p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
                      <span>{film.type === 'movie' ? '电影' : '电视剧'}</span>
                      <span className="w-1 h-1 rounded-full bg-background-400" />
                      <span>{film.year}</span>
                      <span className="w-1 h-1 rounded-full bg-background-400" />
                      <span>{film.genres.slice(0, 2).join(' · ')}</span>
                    </div>
                    <p className="text-xs text-foreground-600 line-clamp-2">{film.synopsis}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center rounded-lg bg-background-100 border border-background-200">
              <span className="flex items-center justify-center w-14 h-14 rounded-full bg-background-200 text-foreground-400 mb-3">
                <i className="ri-inbox-line text-2xl" />
              </span>
              <p className="text-sm text-foreground-500">暂无相关作品资料</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}